import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Typography, useTheme } from "@mui/material";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { chartWindow } from "./chartWindow";
import { chartValueLabels } from "./chartValueLabels";
import { aggregateEffectiveness } from "../../utils/gerenciaModel";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);
const options = {
  responsive: true,
  animation: false,
  maintainAspectRatio: false,
  layout: { padding: { top: 18, right: 12 } },
  plugins: { legend: { display: false } },
  scales: {
    y: { beginAtZero: true, suggestedMax: 100, ticks: { callback: (value) => `${value}%` } },
    x: {
      type: "category",
      offset: true,
      grid: { display: false },
      ticks: { autoSkip: false, maxRotation: 45 },
    },
  },
};
export const GraficosEfectividad = ({ datos = [], vistaActual = "aceptacion" }) => {
  const theme = useTheme();
  const chartOptions = useMemo(
    () => ({
      ...options,
      plugins: {
        ...options.plugins,
        gerenciaValueLabels: {
          color: theme.palette.text.primary,
          halo: theme.palette.background.paper,
        },
      },
    }),
    [theme]
  );
  const charts = useMemo(
    () =>
      [
        { title: "Efectividad por fecha", field: "fecha", line: true },
        {
          title: vistaActual === "recaudo" ? "Por tipo de fuente" : "Por tipo de pasarela",
          field: vistaActual === "recaudo" ? "tipo_fuente" : "tipopasarela",
        },
        { title: "Por pasarela", field: "pasarela" },
        {
          title: vistaActual === "recaudo" ? "Por servicio" : "Por franquicia",
          field: vistaActual === "recaudo" ? "servicio" : "franquicia",
        },
      ].map((chart) => {
        const groups = aggregateEffectiveness(datos, vistaActual, chart.field).filter(
          (group) => group.total > 0
        );
        return { ...chart, groups: chart.line ? groups : groups.slice(0, 10) };
      }),
    [datos, vistaActual]
  );
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" },
        gap: 2,
        mb: 2.5,
      }}
    >
      {charts.map((chart) => (
        <ChartCard key={chart.field} chart={chart} chartOptions={chartOptions} />
      ))}
    </Box>
  );
};
GraficosEfectividad.propTypes = { datos: PropTypes.array, vistaActual: PropTypes.string };

function ChartCard({ chart, chartOptions }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [chart.groups]);
  const visible = chartWindow(chart.groups, chart.line, page);
  const Chart = chart.line ? Line : Bar;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, minWidth: 0 }}>
      <Typography variant="subtitle2" fontWeight="bold">
        {chart.title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {chart.line
          ? "Exitosas / total de transacciones"
          : "Hasta 10 grupos con mayor volumen · porcentaje ponderado"}
      </Typography>
      <Box sx={{ mt: 2, overflowX: "auto" }}>
        {chart.groups.length ? (
          <Box sx={{ height: 260, minWidth: Math.max(320, visible.groups.length * 88 + 80) }}>
            <Chart
              plugins={[chartValueLabels]}
              role="img"
              aria-label={chart.title}
              options={chartOptions}
              data={{
                labels: visible.groups.map((group) => group.label),
                datasets: [
                  {
                    label: "Efectividad (%)",
                    data: visible.groups.map((group) => Number(group.value.toFixed(2))),
                    borderColor: "#1a73e8",
                    backgroundColor: "rgba(26,115,232,.65)",
                    borderWidth: 2,
                    pointRadius: 3,
                  },
                ],
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: "grid", placeItems: "center", height: 240 }}>
            <Typography variant="body2" color="text.secondary">
              Sin transacciones para calcular efectividad.
            </Typography>
          </Box>
        )}
      </Box>
      {chart.line && visible.totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mt: 1.5,
          }}
        >
          <Button
            size="small"
            disabled={visible.page === visible.totalPages - 1}
            onClick={() => setPage(visible.page + 1)}
            sx={{ textTransform: "none" }}
          >
            Fechas anteriores
          </Button>
          <Typography variant="caption" color="text.secondary">
            Fechas {visible.start + 1}–{visible.end} de {chart.groups.length}
          </Typography>
          <Button
            size="small"
            disabled={visible.page === 0}
            onClick={() => setPage(visible.page - 1)}
            sx={{ textTransform: "none" }}
          >
            Fechas siguientes
          </Button>
        </Box>
      )}
    </Paper>
  );
}
ChartCard.propTypes = {
  chart: PropTypes.object.isRequired,
  chartOptions: PropTypes.object.isRequired,
};
